import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  ColumnLayout,
  FormField,
  Input,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { defaultWrapLinesPreference, paginationLabels } from 'src/commons/tables';

import * as validate from 'src/commons/validationUtils';
import { isGlobalTemplate } from 'src/components/utils/template';
import { EmptyState } from 'src/commons/EmptyState';

import { PageHeader } from 'src/components/common/PageHeader';

export interface TemplateResourcesTableProps {
  templateResources: any;
  tableMessage: string;
  notification;
  handleDissociateResource;
}

const TemplateResourcesTable = (props: TemplateResourcesTableProps) => {
  const { templateResources, tableMessage, handleDissociateResource } = props;

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const [dissociateModalVisible, setDissociateModalVisible] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any>('');
  const [buttonLoading] = useState(false);
  const [selectedResourceTemplateId, setSelectedResourceTemplateId] = useState('');
  const [selectedTemplateResourceId, setSelectedTemplateResourceId] = useState('');

  const templateResourcesColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'resourceId',
      header: 'Resource',
      cell: (template) => template.resourceId,
      minWidth: 200,
      sortingField: 'resourceId',
    },
    {
      id: 'scope',
      header: 'Resource type',
      cell: (template) => template.scope,
      minWidth: 200,
      sortingField: 'scope',
    },
    {
      id: 'simTemplateName',
      header: 'SIM template name',
      cell: (template) =>
        template.accessManagementInfo?.simTemplates?.[0]?.simTemplateName
          ? template.accessManagementInfo?.simTemplates?.[0]?.simTemplateName
          : '-',
      minWidth: 200,
    },
    {
      id: 'approvalTemplateTitle',
      header: 'Approval template title',
      cell: (template) =>
        template.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateTitle
          ? template.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateTitle
          : '-',
      minWidth: 200,
    },
  ];

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    templateResources,
    {
      filtering: {
        noMatch: '',
        empty: <EmptyState title={tableMessage} subtitle={'No templates to display.'} />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [],
      },
    },
  );

  useEffect(() => {
    const { selectedItems } = collectionProps;
    const selectedResource: any = selectedItems[0];
    if (selectedResource !== undefined) {
      setSelectedResource(selectedResource);
      setSelectedResourceTemplateId(selectedResource.templateId);
      setSelectedTemplateResourceId(selectedResource.resourceId);
    }
  }, [collectionProps.selectedItems]);

  return (
    <>
      <Table
        {...collectionProps}
        selectionType='single'
        loadingText='Loading template resources...'
        columnDefinitions={templateResourcesColumnDefinitions}
        items={items}
        wrapLines={false}
        resizableColumns={true}
        header={
          <>
            <PageHeader
              buttons={[
                {
                  text: 'Dissociate resource',
                  variant: 'primary',
                  // Table is single selectionType
                  disabled: !collectionProps.selectedItems.length || isGlobalTemplate(collectionProps.selectedItems[0]),
                  onItemClick: () => {
                    setDissociateModalVisible(true);
                  },
                },
              ]}
              header={'Associated resources'}
              description={'This table lists the resources that are associated to the selected template.'}
            />
          </>
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        empty={
          <Box textAlign='center' color='inherit'>
            <b>No resources</b>
            <Box variant='p' color='inherit'>
              No resources to display.
            </Box>
          </Box>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel='Filter resources'
            filteringPlaceholder='Find resources'
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 5, label: '5 items' },
                { value: 10, label: '10 items' },
                { value: 25, label: '25 items' },
                { value: 50, label: '50 items' },
              ],
            }}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
      />
      <Modal
        onDismiss={() => {
          setDissociateModalVisible(false);
        }}
        visible={dissociateModalVisible}
        size='medium'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setDissociateModalVisible(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                disabled={!validate.isNotFalsy(selectedTemplateResourceId)}
                loading={buttonLoading}
                onClick={() => {
                  setDissociateModalVisible(false);
                  handleDissociateResource(selectedResourceTemplateId, selectedTemplateResourceId);
                }}
              >
                {'Dissociate'}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Dissociate resource from template'
      >
        <ColumnLayout>
          <FormField label={'Resource ID'}>
            <Input ariaRequired={true} value={selectedResource.resourceId} readOnly={true} disabled />
          </FormField>
          <FormField label={'Resource Type'}>
            <Input ariaRequired={true} value={selectedResource.scope} readOnly={true} disabled />
          </FormField>
        </ColumnLayout>
      </Modal>
    </>
  );
};

export default TemplateResourcesTable;
